'use client';

import { useCallback, useMemo } from 'react';

import type { ProposedAction } from '@/entities/proposed-actions';
import type { ProposedActionPayloadTToolInput } from '@lotushealth/lotushealth_js_sdk/schemas';
import { openDosespotActionOverlay } from '@/features/dosespot-action-form/model/dosespotActionPrefill';
import { openRxWizardOverlay } from '@/features/rx-form/model/rxWizardPrefill';
import { openLabOrderWizardOverlay } from '@/features/lab-order-form/model/labOrderWizardPrefill';
import {
  openPriorAuthActionOverlay,
  type PriorAuthActionMode,
} from '@/features/prior-auth-form/model/priorAuthActionPrefill';
import { openPaCompletedRxViewOverlay } from '@/features/pa-completed-rx-view/model/paCompletedRxViewPrefill';
import {
  useProposedActionMutations,
  type ProposedActionMutations,
} from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import type { SourceProposedActionT } from '@/shared/lib';
import { logger } from '@/shared/lib';
import { getTodayYYYYMMDD } from '@/shared/lib/dates';

const DOSESPOT_TOOL_NAMES = [
  'approve_refill',
  'deny_refill',
  'replace_refill',
  'approve_rxchange',
  'approve_rxchange_with_changes',
  'deny_rxchange',
] as const;

const DOSESPOT_TOOLS = new Set<string>(DOSESPOT_TOOL_NAMES);

const PA_ACTION_TOOL_TO_MODE = {
  answer_prior_auth_question: 'answer_question',
  upload_prior_auth_attachment: 'upload_attachment',
  submit_prior_auth: 'submit',
  cancel_prior_auth: 'cancel',
  appeal_prior_auth: 'appeal',
  deny_prior_auth: 'deny',
} as const satisfies Record<string, PriorAuthActionMode>;

type PaActionToolName = keyof typeof PA_ACTION_TOOL_TO_MODE;

const PA_ACTION_TOOLS = new Set<string>(Object.keys(PA_ACTION_TOOL_TO_MODE));

function isPaActionToolName(toolName: string): toolName is PaActionToolName {
  return PA_ACTION_TOOLS.has(toolName);
}

const PA_COMPLETED_TOOL = 'review_completed_prior_auth';
const PA_SEND_APPROVED_TOOL = 'send_approved_prior_auth_to_pharmacy';

const PA_COMPLETED_VIEW_TOOLS = new Set<string>([PA_COMPLETED_TOOL, PA_SEND_APPROVED_TOOL]);

const CLIENT_TOOLS = new Set<string>([
  'draft_prescription',
  'draft_existing_lab',
  'send_follow_up',
  ...DOSESPOT_TOOL_NAMES,
  ...PA_ACTION_TOOLS,
  PA_COMPLETED_TOOL,
  PA_SEND_APPROVED_TOOL,
]);

export interface ProposedActionExecutor {
  execute: (action: ProposedAction) => void;
  revise: (action: ProposedAction, revisionReason: string) => void;
  hasClientExecution: (toolName: string) => boolean;
}

type RevisionContext = Readonly<{ isRevision: true; revisionReason: string }>;

function toNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toStr(value: unknown): string {
  return typeof value === 'string' ? value : String(value ?? '');
}

function buildSource(externalId: string, revision?: RevisionContext): SourceProposedActionT {
  return revision
    ? { externalId, isRevision: true, revisionReason: revision.revisionReason }
    : { externalId };
}

function openRxWizard(
  input: ProposedActionPayloadTToolInput,
  externalId: string,
  revision?: RevisionContext
): void {
  openRxWizardOverlay({
    kind: 'coded',
    dispensableDrugId: toNumber(input.dispensableDrugId, 0),
    directions: toStr(input.directions),
    quantity: toStr(input.quantity),
    refills: toStr(input.refills),
    daysSupply: toStr(input.daysSupply),
    pharmacyNotes: input.pharmacyNotes != null ? toStr(input.pharmacyNotes) : undefined,
    noSubstitutions: input.noSubstitutions === true,
    effectiveDate: getTodayYYYYMMDD(),
    sourceProposedAction: buildSource(externalId, revision),
  });
}

function openLabWizard(
  input: ProposedActionPayloadTToolInput,
  externalId: string,
  revision?: RevisionContext
): void {
  const rawIcdCodes = input.icdCodes;
  const icdCodes = Array.isArray(rawIcdCodes)
    ? rawIcdCodes
        .filter(
          (c): c is { code: string; description: string } =>
            typeof c === 'object' &&
            c !== null &&
            typeof (c as Record<string, unknown>).code === 'string'
        )
        .map((c) => ({ code: c.code, description: c.description ?? '' }))
    : undefined;

  openLabOrderWizardOverlay({
    labTestId: toStr(input.labTestId),
    labTestName: input.labTestName != null ? toStr(input.labTestName) : undefined,
    icdCodes,
    notes: input.notes != null ? toStr(input.notes) : undefined,
    priority: input.priority === true,
    sourceProposedAction: buildSource(externalId, revision),
  });
}

function openDosespotActionForm(action: ProposedAction, revision?: RevisionContext): void {
  openDosespotActionOverlay({
    action,
    sourceProposedAction: buildSource(action.external_id, revision),
  });
}

function openPriorAuthActionForm(
  action: ProposedAction,
  mode: PriorAuthActionMode,
  revision?: RevisionContext
): void {
  const input = action.payload.tool_input;
  const caseId = toNumber(input.priorAuthId, 0);
  const recordId = input.priorAuthRecordId != null ? toStr(input.priorAuthRecordId) : '';
  const pendingQuestionIdRaw = input.pendingQuestionId;
  const pendingQuestionId =
    pendingQuestionIdRaw != null ? toNumber(pendingQuestionIdRaw, 0) || null : null;

  openPriorAuthActionOverlay({
    caseId,
    recordId,
    mode,
    pendingQuestionId,
    patientUserId: input.patientUserId != null ? toStr(input.patientUserId) : undefined,
    dosespotPrescriptionId:
      input.dosespotPrescriptionId != null ? toStr(input.dosespotPrescriptionId) : undefined,
    action,
    sourceProposedAction: buildSource(action.external_id, revision),
  });
}

function openPaCompletedRxView(action: ProposedAction, revision?: RevisionContext): void {
  const input = action.payload.tool_input;
  openPaCompletedRxViewOverlay({
    patientUserId: input.patientUserId != null ? toStr(input.patientUserId) : '',
    priorAuthRecordId: input.priorAuthRecordId != null ? toStr(input.priorAuthRecordId) : '',
    prescriptionRecordId:
      input.prescriptionRecordId != null ? toStr(input.prescriptionRecordId) : '',
    dosespotPrescriptionId:
      input.dosespotPrescriptionId != null ? toStr(input.dosespotPrescriptionId) : '',
    authorizationNumber:
      input.authorizationNumber != null ? toStr(input.authorizationNumber) : null,
    status: input.status != null ? toStr(input.status) : '',
    sourceProposedAction: buildSource(action.external_id, revision),
    approvedQuantity: input.approvedQuantity != null ? toStr(input.approvedQuantity) : null,
    approvedDaysSupply: input.approvedDaysSupply != null ? toStr(input.approvedDaysSupply) : null,
    approvedRefills: input.approvedRefills != null ? toStr(input.approvedRefills) : null,
  });
}

function routeTool(
  mode: 'execute',
  action: ProposedAction,
  mutations: Pick<ProposedActionMutations, 'execute' | 'rejectWithRevision'>
): void;
function routeTool(
  mode: 'revise',
  action: ProposedAction,
  mutations: Pick<ProposedActionMutations, 'execute' | 'rejectWithRevision'>,
  revisionReason: string
): void;
function routeTool(
  mode: 'execute' | 'revise',
  action: ProposedAction,
  mutations: Pick<ProposedActionMutations, 'execute' | 'rejectWithRevision'>,
  revisionReason?: string
): void {
  const { tool_name: toolName, tool_input: input } = action.payload;
  const externalId = action.external_id;
  const revision: RevisionContext | undefined =
    mode === 'revise' ? { isRevision: true, revisionReason: revisionReason! } : undefined;

  if (DOSESPOT_TOOLS.has(toolName)) {
    openDosespotActionForm(action, revision);
    logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
    return;
  }

  if (isPaActionToolName(toolName)) {
    openPriorAuthActionForm(action, PA_ACTION_TOOL_TO_MODE[toolName], revision);
    logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
    return;
  }

  if (PA_COMPLETED_VIEW_TOOLS.has(toolName)) {
    openPaCompletedRxView(action, revision);
    logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
    return;
  }

  switch (toolName) {
    case 'draft_prescription':
      openRxWizard(input, externalId, revision);
      break;
    case 'draft_existing_lab':
      openLabWizard(input, externalId, revision);
      break;
    case 'send_follow_up': {
      const call =
        mode === 'revise'
          ? mutations.rejectWithRevision(externalId)
          : mutations.execute(externalId);
      void call.catch((err: unknown) => {
        logger.error(`proposed-actions.${mode}.sendFollowUpFailed`, { toolName, externalId, err });
      });
      break;
    }
    default:
      logger.warn(`proposed-actions.${mode}.unsupported`, { toolName, externalId });
      return;
  }

  logger.info(`proposed-actions.${mode}.triggered`, { toolName, externalId });
}

export function useProposedActionExecutor(): ProposedActionExecutor {
  const mutations = useProposedActionMutations();

  const hasClientExecution = useCallback(
    (toolName: string): boolean => CLIENT_TOOLS.has(toolName),
    []
  );

  const execute = useCallback(
    (action: ProposedAction): void => routeTool('execute', action, mutations),
    [mutations]
  );

  const revise = useCallback(
    (action: ProposedAction, revisionReason: string): void =>
      routeTool('revise', action, mutations, revisionReason),
    [mutations]
  );

  return useMemo(
    () => ({ execute, revise, hasClientExecution }),
    [execute, revise, hasClientExecution]
  );
}
